import * as yup from 'yup'

const priceRangeSchema = yup.object().shape({
    price_start: yup
        .mixed()
        .required('services.validation.price-start-required')
        .test(
            'is-number',
            'services.validation.price-start-number',
            (value) => {
                return !isNaN(Number(value)) && Number(value) >= 0
            }
        ),
    price_end: yup
        .mixed()
        .required('services.validation.price-end-required')
        .test('is-number', 'services.validation.price-end-number', (value) => {
            return !isNaN(Number(value)) && Number(value) >= 0
        })
        .test(
            'greater-than-start',
            'services.validation.price-end-greater',
            function (value) {
                const { price_start } = this.parent
                return Number(value) > Number(price_start)
            }
        ),
    price_per_unit: yup
        .mixed()
        .required('services.validation.price-per-unit-required')
        .test(
            'is-number',
            'services.validation.price-per-unit-number',
            (value) => {
                return !isNaN(Number(value)) && Number(value) >= 0
            }
        ),
})

const serviceConfigoptionSchema = yup.object().shape({
    service_configoption_id: yup.number().required(),
    price_exceed_id: yup.number().nullable(),
    name: yup.string().required(),
    method: yup.object().shape({
        calc_amount_method: yup
            .string()
            .required('services.validation.calc-method-required'),
    }),
    price_ranges: yup
        .array()
        .of(priceRangeSchema)
        .min(1, 'services.validation.price-ranges-min')
        .test(
            'continuous-ranges',
            'services.validation.price-ranges-continuous',
            function (ranges) {
                if (!ranges || ranges.length === 0) return true

                // Sort ranges by price_start
                const sortedRanges = [...ranges].sort(
                    (a, b) => Number(a.price_start) - Number(b.price_start)
                )

                // Check if first range starts from 0
                if (Number(sortedRanges[0].price_start) !== 0) {
                    return this.createError({
                        message:
                            'services.validation.first-range-must-start-zero',
                        path: `${this.path}[0].price_start`,
                    })
                }

                // Check continuity between ranges
                for (let i = 0; i < sortedRanges.length - 1; i++) {
                    const currentRange = sortedRanges[i]
                    const nextRange = sortedRanges[i + 1]

                    // Current range end should equal next range start
                    if (
                        Number(currentRange.price_end) !==
                        Number(nextRange.price_start)
                    ) {
                        return this.createError({
                            message:
                                'services.validation.ranges-not-continuous',
                            path: `${this.path}[${i + 1}].price_start`,
                        })
                    }
                }

                // Check if last range ends with unlimited (999999999)
                const lastRange = sortedRanges[sortedRanges.length - 1]
                if (Number(lastRange.price_end) < 999999999) {
                    return this.createError({
                        message:
                            'services.validation.last-range-must-be-unlimited',
                        path: `${this.path}[${sortedRanges.length - 1}].price_end`,
                    })
                }

                return true
            }
        )
        .test(
            'no-overlapping-ranges',
            'services.validation.price-ranges-no-overlap',
            function (ranges) {
                if (!ranges || ranges.length === 0) return true

                // Sort ranges by price_start
                const sortedRanges = [...ranges].sort(
                    (a, b) => Number(a.price_start) - Number(b.price_start)
                )

                // Check for overlapping ranges
                for (let i = 0; i < sortedRanges.length - 1; i++) {
                    const currentRange = sortedRanges[i]
                    const nextRange = sortedRanges[i + 1]

                    // Current range end should not be greater than next range start
                    if (
                        Number(currentRange.price_end) >
                        Number(nextRange.price_start)
                    ) {
                        return this.createError({
                            message: 'services.validation.ranges-overlapping',
                            path: `${this.path}[${i}].price_end`,
                        })
                    }
                }

                return true
            }
        )
        .test(
            'no-duplicate-ranges',
            'services.validation.price-ranges-no-duplicate',
            function (ranges) {
                if (!ranges || ranges.length === 0) return true

                // Check for duplicate ranges (same start and end)
                for (let i = 0; i < ranges.length; i++) {
                    for (let j = i + 1; j < ranges.length; j++) {
                        const range1 = ranges[i]
                        const range2 = ranges[j]

                        if (
                            Number(range1.price_start) ===
                                Number(range2.price_start) &&
                            Number(range1.price_end) ===
                                Number(range2.price_end)
                        ) {
                            return this.createError({
                                message: 'services.validation.ranges-duplicate',
                                path: `${this.path}[${j}].price_start`,
                            })
                        }
                    }
                }

                return true
            }
        ),
})

const configPriceRangesSchema = yup.object().shape({
    service_configoptions: yup
        .array()
        .of(serviceConfigoptionSchema)
        .min(1, 'services.validation.service-configoptions-required'),
})

export default configPriceRangesSchema
